'use client';
import { RESPONSIVE_MARGIN } from '@/functions/createTheme';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { AreaSelect } from './AreaSelect';
import { CategorySelect } from './CategorySelect';
import { DatePicker } from './DatePicker';
import { KeywordInput } from './KeywordInput';
import { useSearchQuery } from './hooks';

type Props = {
  adComponent: React.ReactNode;
  searchParams: SearchParams;
  customerSetting: CustomerSetting;
};

export const EventSearch: React.FC<Props> = ({
  adComponent,
  searchParams,
  customerSetting
}) => {
  const theme = useTheme();

  const {
    sort,
    keyword,
    dates,
    categoryId,
    areaId,
    setKeyword,
    setDates,
    setCategoryId,
    setAreaId,
    ref,
    areaEnabled,
    gridItemSm,
    makeSearchButtonUrl,
    makeSortSearchUrl
  } = useSearchQuery(customerSetting, searchParams);

  return (
    <Box ref={ref} sx={{ my: RESPONSIVE_MARGIN }}>
      <Box sx={{ my: 1 }}>
        <Typography
          component="h2"
          variant="h5"
          sx={{
            fontSize: {
              xs: theme.typography.h6.fontSize,
              sm: theme.typography.h5.fontSize
            }
          }}
        >
          イベントを探す
        </Typography>
      </Box>
      <Grid container spacing={1}>
        <Grid size={{ xs: 12, sm: gridItemSm }}>
          <KeywordInput keyword={keyword} setKeyword={setKeyword} />
        </Grid>
        <Grid size={{ xs: 12, sm: gridItemSm }}>
          <DatePicker dates={dates} setDates={setDates} />
        </Grid>
        <Grid size={{ xs: 12, sm: gridItemSm }}>
          <CategorySelect
            categoryId={categoryId}
            setCategoryId={setCategoryId}
            customerSetting={customerSetting}
          />
        </Grid>
        {areaEnabled && (
          <Grid size={{ xs: 12, sm: gridItemSm }}>
            <AreaSelect
              areaId={areaId}
              setAreaId={setAreaId}
              customerSetting={customerSetting}
            />
          </Grid>
        )}
      </Grid>
      <Grid container sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Grid size={{ xs: 12, sm: gridItemSm }} sx={{ width: '100%' }}>
          <Button
            LinkComponent="a"
            fullWidth
            variant="contained"
            sx={{ fontSize: '1.2rem' }}
            href={makeSearchButtonUrl()}
          >
            絞り込む
          </Button>
        </Grid>
      </Grid>
      {/* 広告の表示 */}
      {adComponent}
      {/* ソート方式のトグル */}
      <Box
        display="flex"
        justifyContent="flex-end"
        sx={{ my: RESPONSIVE_MARGIN }}
      >
        <ToggleButtonGroup color="primary" size="small" value={sort} exclusive>
          <ToggleButton
            value="date"
            LinkComponent="a"
            aria-pressed={undefined}
            href={makeSortSearchUrl('date')}
          >
            開催日が近い順
          </ToggleButton>
          <ToggleButton
            value="new"
            LinkComponent="a"
            aria-pressed={undefined}
            href={makeSortSearchUrl('new')}
          >
            新着順
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
};
